import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthToken, getLocationId, isAuthenticated, setAuthData, clearSession } from './session';

/**
 * Authentication Context for GHL SSO
 * 
 * Provides auth state and methods to React components.
 * Requirements: 8.2
 */

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  locationId: string | null;
  login: (token: string, locationId: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState({
    isAuthenticated: isAuthenticated(),
    token: getAuthToken(),
    locationId: getLocationId(),
  });

  useEffect(() => {
    // Listen for SSO authentication events
    function handleSSOAuth(event: CustomEvent<{ token: string; locationId: string }>) {
      setAuthState({
        isAuthenticated: true,
        token: event.detail.token,
        locationId: event.detail.locationId,
      });
    }

    window.addEventListener('sso-authenticated', handleSSOAuth as EventListener);
    
    return () => {
      window.removeEventListener('sso-authenticated', handleSSOAuth as EventListener);
    };
  }, []);

  function login(token: string, locationId: string) {
    setAuthData(token, locationId);
    setAuthState({
      isAuthenticated: true,
      token,
      locationId,
    });
  }

  function logout() {
    clearSession();
    setAuthState({
      isAuthenticated: false,
      token: null,
      locationId: null,
    });
  }

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
